/**
 * 商店面板（购买 + 出售）
 */

import { ITEMS, type GameItem } from '../data/items';
import { getCurrentPrice } from '../engine/MarketSystem';
import type { PlayerState } from '../engine/Player';

export type ShopAction = 'buy' | 'sell' | 'close';

export interface ShopActionPayload {
  action: ShopAction;
  itemId?: string;
  quantity?: number;
}

export class ShopPanel {
  private overlay: HTMLElement | null = null;
  private onAction: ((payload: ShopActionPayload) => void) | null = null;
  private tab: 'buy' | 'sell' = 'buy';

  setOnAction(callback: (payload: ShopActionPayload) => void): void {
    this.onAction = callback;
  }

  open(player: PlayerState): void {
    this.close();
    const spiritStones = player.spiritStones;

    this.overlay = document.createElement('div');
    this.overlay.className = 'modal-overlay';

    // 购买列表
    const buyHtml = ITEMS.map((item) => {
      if (item.price === 0) return '';
      const curPrice = getCurrentPrice(item.price);
      const maxAfford = Math.floor(spiritStones / curPrice);
      return `
        <div class="shop-item ${maxAfford > 0 ? '' : 'cant-afford'}">
          <div class="shop-item-info">
            <span class="shop-item-name">${this.itemIcon(item)} ${escapeHtml(item.name)}</span>
            <span class="shop-item-desc">${this.getItemDesc(item)}</span>
          </div>
          <div class="shop-item-right">
            <span class="shop-item-price">💰${curPrice}</span>
            <div style="display:flex;align-items:center;gap:4px;">
              <input type="number" class="shop-qty" data-item="${item.id}" data-price="${curPrice}"
                value="1" min="1" max="${maxAfford}" style="width:50px;padding:3px 4px;font-size:0.8em;
                background:var(--bg-input);color:var(--text-primary);border:1px solid var(--border-color);border-radius:3px;text-align:center;">
              <button class="btn btn-small ${maxAfford > 0 ? 'btn-primary' : ''} shop-buy-btn"
                data-item="${item.id}"
                ${maxAfford > 0 ? '' : 'disabled'}>
                ${maxAfford > 0 ? '购买' : '不足'}
              </button>
            </div>
          </div>
        </div>
      `;
    }).join('');

    // 出售列表（背包物品按50%市价出售）
    const sellItems = this.groupInventory(player);
    const sellHtml = sellItems.length === 0
      ? '<div style="color:var(--text-secondary);text-align:center;padding:20px;">背包空空，没有什么可卖的</div>'
      : sellItems.map(({ item, count }) => {
        const basePrice = item.price;
        const sellPrice = Math.floor(getCurrentPrice(basePrice) * 0.5);
        if (sellPrice <= 0) return '';
        return `
          <div class="shop-item">
            <div class="shop-item-info">
              <span class="shop-item-name">${this.itemIcon(item)} ${escapeHtml(item.name)}</span>
              <span class="shop-item-desc">持有：${count}</span>
            </div>
            <div class="shop-item-right">
              <span class="shop-item-price" style="color:var(--success);">💰${sellPrice}</span>
              <div style="display:flex;align-items:center;gap:4px;">
                <input type="number" class="sell-qty" data-item="${item.id}" data-price="${sellPrice}"
                  value="1" min="1" max="${count}" style="width:50px;padding:3px 4px;font-size:0.8em;
                  background:var(--bg-input);color:var(--text-primary);border:1px solid var(--border-color);border-radius:3px;text-align:center;">
                <button class="btn btn-small sell-btn" data-item="${item.id}">出售</button>
              </div>
            </div>
          </div>
        `;
      }).join('');

    this.overlay.innerHTML = `
      <div class="modal shop-modal">
        <h2>🏪 坊市</h2>
        <div style="margin-bottom:8px; color:var(--text-secondary)">
          当前灵石：<span style="color:var(--text-gold);font-weight:bold;">${spiritStones}</span>
        </div>
        <div style="display:flex;gap:8px;margin-bottom:8px;">
          <button class="btn btn-small ${this.tab === 'buy' ? 'btn-primary' : ''}" id="shop-tab-buy">🛒 购买</button>
          <button class="btn btn-small ${this.tab === 'sell' ? 'btn-primary' : ''}" id="shop-tab-sell">💸 出售</button>
        </div>
        <div class="shop-items-list">${this.tab === 'buy' ? buyHtml : sellHtml}</div>
        <div style="margin-top:16px;">
          <button class="btn" data-action="close">关闭坊市</button>
        </div>
      </div>
    `;

    document.body.appendChild(this.overlay);

    // 绑定购买
    this.overlay.querySelectorAll('.shop-buy-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const itemId = (btn as HTMLElement).dataset.item!;
        const row = (btn as HTMLElement).closest('.shop-item')!;
        const qtyInput = row.querySelector('.shop-qty') as HTMLInputElement;
        const qty = parseInt(qtyInput?.value || '1');
        if (this.onAction) this.onAction({ action: 'buy', itemId, quantity: qty });
      });
    });

    // 绑定出售
    this.overlay.querySelectorAll('.sell-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const itemId = (btn as HTMLElement).dataset.item!;
        const row = (btn as HTMLElement).closest('.shop-item')!;
        const qtyInput = row.querySelector('.sell-qty') as HTMLInputElement;
        const qty = parseInt(qtyInput?.value || '1');
        if (this.onAction) this.onAction({ action: 'sell', itemId, quantity: qty });
      });
    });

    // 标签切换
    document.getElementById('shop-tab-buy')?.addEventListener('click', () => { this.tab = 'buy'; this.open(player); });
    document.getElementById('shop-tab-sell')?.addEventListener('click', () => { this.tab = 'sell'; this.open(player); });

    document.querySelector('button[data-action="close"]')?.addEventListener('click', () => {
      this.close();
      if (this.onAction) this.onAction({ action: 'close' });
    });
    this.overlay.addEventListener('click', (e) => { if (e.target === this.overlay) { this.close(); if (this.onAction) this.onAction({ action: 'close' }); } });
  }

  private groupInventory(player: PlayerState): { item: GameItem; count: number }[] {
    const map = new Map<string, { item: GameItem; count: number }>();
    for (const i of player.inventory) {
      const existing = map.get(i.id);
      if (existing) existing.count++;
      else map.set(i.id, { item: i, count: 1 });
    }
    return [...map.values()].filter((e) => e.item.price > 0);
  }

  close(): void { if (this.overlay) { this.overlay.remove(); this.overlay = null; } }
  isOpen(): boolean { return this.overlay !== null; }

  private itemIcon(item: GameItem): string {
    switch (item.type) { case 'consumable': return '🧪'; case 'equipment': return '⚔️'; case 'material': return '🌿'; default: return '📦'; }
  }
  private getItemDesc(item: GameItem): string {
    if (item.type === 'equipment') { const p: string[] = []; if (item.attackBonus) p.push(`攻+${item.attackBonus}`); if (item.defenseBonus) p.push(`防+${item.defenseBonus}`); return p.join(' '); }
    return item.description;
  }
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
